import type { IStripeAccount } from '@medely/types';
import { useProNavigate } from '@mp/contexts';
import useStates from '@mp/hooks/useStates';
import useTaxClassifications from '@mp/hooks/useTaxClassifications';
import useTaxInfo from '@mp/hooks/useTaxInfo';
import type { NotificationT } from './types';
import { toPhoneNumber } from '@mp/utils/toPhoneNumber';
import L from 'lodash';
import { INDIVIDUAL_TAX_CLASSIFICATION_ID } from '@mp/constants/tax';
import type { TaxInfo } from '@mp/models/settings/tax-info/TaxInfo.schema';

type useTaxInfoTabProps = {
  account: IStripeAccount | null;
  notification: NotificationT;
};

export function useTaxInfoTab({ account, notification }: useTaxInfoTabProps) {
  const { taxClassifications } = useTaxClassifications();
  const { addTaxInfo, editTaxInfo } = useTaxInfo();
  const { navigate } = useProNavigate();

  const taxClassificationOptions = taxClassifications
    .filter((taxClassification) => taxClassification.label.toLowerCase() !== 'individual')
    .map((l) => ({ value: l.id, label: l.label }));

  const isEditing = !!account?.id;

  const { states } = useStates();
  const stateOptions = states.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  function handleGoBack() {
    navigate('PayoutTaxes');
  }

  async function onSubmit(values: TaxInfo) {
    const isIndividual = Number(values.tax_classification_id) === INDIVIDUAL_TAX_CLASSIFICATION_ID;
    const payload = {
      ...values,
      representative: L.omit(
        {
          ...values.representative,
          phone: toPhoneNumber((values?.representative as any)?.phone) || '',
        },
        [
          Number(values.tax_classification_id) === 2 || Number(values.tax_classification_id) === 4
            ? 'ssn_last_4'
            : 'id_number',
          'show_full_ssn',
        ],
      ),
    };

    if (isEditing) {
      const editPayload = {
        id: account.id,
        ...payload,
      };

      if (isIndividual) {
        delete (editPayload as any).representative;
      } else {
        editPayload.representative = L.omit(editPayload.representative, 'id');
      }

      await editTaxInfo(editPayload, {
        onSuccess: () => {
          handleGoBack();

          notification.success('Tax form is successfully updated');
        },
      });
    } else {
      if (isIndividual) {
        delete (payload as any).representative;
      } else {
        payload.representative = {
          ...payload.representative,
          phone: toPhoneNumber((payload?.representative as any)?.phone) || '',
        };
      }

      await addTaxInfo(payload, {
        onSuccess: () => {
          handleGoBack();

          notification.success('New tax form is successfully added');
        },
      });
    }
  }

  return {
    taxClassificationOptions,
    stateOptions,
    onSubmit,
    handleGoBack,
  };
}
